"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAnnotationCanvasDataflow = void 0;
var tslib_1 = require("tslib");
var operators_1 = require("rxjs/operators");
var utils_1 = require("../../../utils");
var use_callbacks_1 = require("../use-callbacks");
var use_extracted_values_1 = require("../use-extracted-values");
var use_annotation_canvas_mode_1 = require("./use-annotation-canvas-mode");
var use_dataflow_of_relative_values_1 = require("./use-dataflow-of-relative-values");
var use_dataflow_of_zoom_1 = require("./use-dataflow-of-zoom");
// import { useEventsFiltered } from './use-events-filtered'
var use_visible_area_absolute_values_1 = require("./use-visible-area-absolute-values");
// import { TPaintObject } from '../../typedefs/paint-object';
/**
 * @description キャンバス上でのマウスイベントを受け取り必要な値を計算する．
 *  位置データは画像の左上を基準とする値で持つ．
 *  （キャンバスに余白を持たせる場合，ポインタ位置は負の値になりうる）
 *  画面のリサイズや画像のズーム・移動に対応するため，内部データは画像を1x1とする相対値で持つ．
 */
var useAnnotationCanvasDataflow = function (
/* configs */
fitPosToGrid, fitRectSizeToGrid, idMaker, styles, 
/* props */
imagePixelSize$, zoomable$, moveImageMode$, annotationMode$, bboxesRelativeInput$, paintObjectsInput$, selectedIdInput$, highlightIdInput$, visibleLabels$, selectedLabel$, action$, 
/* events */
mouseEvent$, mousePos$, // canvas上の位置
wheelScrollDelta$, rootRectSizeAbsolute$, 
/* callbacks */
onBboxesChange, onSelectedIdChange, onHighlightIdChange, onModeChange, onCrop) {
    /**
     * @description
     * （`***Copy$`について）
     * dataflowの下流の要素だが上流側で値を参照する必要があるため用意．
     * 無限ループの発生を防ぐため発火は無視する必要がある．
     * （`withLatestFrom`等の値の参照のみ行うオペレーター内でのみ使用）
     */
    var _a = tslib_1.__read((0, utils_1.useStateAsStream)('background'), 2), canvasObjectRelativeOnCursorCopy$ = _a[0], setCanvasObjectRelativeOnCursorCopy = _a[1];
    var aspectRatio$ = (0, utils_1.useDataStream)(1, imagePixelSize$.pipe((0, operators_1.filter)(function (rectSize) { return rectSize.width > 0 && rectSize.height > 0; }), (0, operators_1.map)(function (rectSize) { return rectSize.width / rectSize.height; })));
    // canvasのサイズ
    var canvasRectSizeAbsolute$ = (0, utils_1.useDataStream)((0, utils_1.IRectSize)(), (0, utils_1.combineLatestTyped)(rootRectSizeAbsolute$, aspectRatio$).pipe((0, operators_1.filter)(function (_a) {
        var _b = tslib_1.__read(_a, 2), _ = _b[0], ar = _b[1];
        return ar > 0;
    }), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), rrs = _b[0], ar = _b[1];
        return (0, utils_1.roundRectSizeToInt)((0, utils_1.toInnerRectSizeKeepingAspectRatio)(rrs, ar));
    })));
    /**
     * @description マウス左ボタンが押されているか
     */
    var mouseLeftIsDown$ = (0, utils_1.useDataStream)(false, (0, utils_1.mergeTyped)(mouseEvent$.pipe((0, operators_1.map)(function (e) {
        switch (e) {
            case 'enter':
            case 'leave':
            case 'left-up':
                return false;
            case 'left-down':
                return true;
            case 'right-down':
            case 'right-up':
                return undefined;
        }
    }), (0, utils_1.filterNotUndefined)()), action$.pipe((0, operators_1.filter)(function (e) { return e.op === 'reset-pointer-pos'; }), (0, operators_1.mapTo)(false))));
    /**
     * @description マウス右ボタンが押されているか
     */
    var mouseRightIsDown$ = (0, utils_1.useDataStream)(false, (0, utils_1.mergeTyped)(mouseEvent$.pipe((0, operators_1.map)(function (e) {
        switch (e) {
            case 'enter':
            case 'leave':
            case 'right-up':
                return false;
            case 'right-down':
                return true;
            case 'left-down':
            case 'left-up':
                return undefined;
        }
    }), (0, utils_1.filterNotUndefined)()), action$.pipe((0, operators_1.filter)(function (e) { return e.op === 'reset-pointer-pos'; }), (0, operators_1.mapTo)(false))));
    /**
     * @description AnnotationCanvasのモード．
     */
    var mode$ = (0, use_annotation_canvas_mode_1.useAnnotationCanvasMode)(annotationMode$, moveImageMode$, mouseRightIsDown$, mouseLeftIsDown$, mouseEvent$.pipe((0, utils_1.filterEnumSubset)(['leave', 'enter'])), canvasObjectRelativeOnCursorCopy$, rootRectSizeAbsolute$);
    var _b = (0, use_visible_area_absolute_values_1.useVisibleAreaAbsoluteValues)(canvasRectSizeAbsolute$, mousePos$, mouseEvent$.pipe((0, utils_1.filterValue)('leave'), (0, operators_1.mapTo)(undefined)), styles.bgMinPaddingPx), imgVisibleAreaRectAbsolute$ = _b.imgVisibleAreaRectAbsolute$, pointerPosOnImgVisibleAreaAbsolute$ = _b.pointerPosOnImgVisibleAreaAbsolute$;
    /**
     * @description
     * ズームや移動をしたあとの画像の仮想的な位置を表す長方形
     */
    var virtualImgRectAbsolute$ = (0, use_dataflow_of_zoom_1.useDataflowOfZoom)(zoomable$, mouseLeftIsDown$, mouseRightIsDown$, wheelScrollDelta$, mode$, imgVisibleAreaRectAbsolute$, pointerPosOnImgVisibleAreaAbsolute$, action$.pipe((0, operators_1.filter)(function (e) { return e.op === 'reset-zoom'; }), (0, operators_1.mapTo)(undefined)), fitPosToGrid);
    /**
     * @description
     * virtualImgRectAbsolute を基準とする、bboxの点の相対サイズ
     */
    var pointHalfSizeRelative$ = (0, utils_1.useDataStream)((0, utils_1.IRectSize)(), virtualImgRectAbsolute$.pipe((0, operators_1.map)(function (virtualImgRectAbsolute) {
        return fitRectSizeToGrid((0, utils_1.toRelativeRectSize)(virtualImgRectAbsolute)((0, utils_1.IRectSize)({
            width: styles.bbox.pointWidthPx,
            height: styles.bbox.pointWidthPx
        })));
    })));
    /**
     * @description
     * virtualImgRectAbsolute を基準とするポインタの相対位置
     */
    var pointerPosRelative$ = (0, utils_1.useDataStream)(undefined, pointerPosOnImgVisibleAreaAbsolute$.pipe((0, operators_1.withLatestFrom)(virtualImgRectAbsolute$.pipe((0, operators_1.map)(utils_1.toRelativePos))), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), pointerPosAbsolute = _b[0], toRelativePosFn = _b[1];
        return pointerPosAbsolute === undefined
            ? undefined
            : fitPosToGrid(toRelativePosFn(pointerPosAbsolute));
    })));
    var pointerPosPixel$ = (0, utils_1.useDataStream)(undefined, pointerPosRelative$.pipe((0, operators_1.withLatestFrom)(imagePixelSize$.pipe((0, operators_1.map)(utils_1.IRect), (0, operators_1.map)(utils_1.toAbsolutePos))), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), pointerPosRelative = _b[0], toAbsolutePosFn = _b[1];
        return pointerPosRelative === undefined
            ? undefined
            : (0, utils_1.roundPosToInt)(toAbsolutePosFn(pointerPosRelative));
    })));
    var _c = (0, use_dataflow_of_relative_values_1.useDataFlowOfRelativeValues)(action$, mouseEvent$, mouseLeftIsDown$, selectedIdInput$, bboxesRelativeInput$, paintObjectsInput$, pointerPosRelative$, pointerPosPixel$, pointHalfSizeRelative$, highlightIdInput$, mode$, visibleLabels$, selectedLabel$, idMaker), cropTemporaryRectRelative$ = _c.cropTemporaryRectRelative$, addBboxTemporaryRectRelative$ = _c.addBboxTemporaryRectRelative$, resizeBboxTemporaryRectRelative$ = _c.resizeBboxTemporaryRectRelative$, selectedId$ = _c.selectedId$, bboxesRelative$ = _c.bboxesRelative$, bboxDatabase$ = _c.bboxDatabase$, highlightId$ = _c.highlightId$, canvasObjectRelativeOnCursor$ = _c.canvasObjectRelativeOnCursor$, cursorType$ = _c.cursorType$, cropRect$ = _c.cropRect$;
    (0, utils_1.useStreamEffect)(canvasObjectRelativeOnCursor$, setCanvasObjectRelativeOnCursorCopy);
    (0, use_callbacks_1.useAnnotationCanvasCallbacks)(bboxDatabase$, selectedId$, highlightId$, mode$, cropRect$, onBboxesChange, onSelectedIdChange, onHighlightIdChange, onModeChange, onCrop);
    var values = (0, use_extracted_values_1.useExtractedValues)(pointerPosRelative$, virtualImgRectAbsolute$, cropTemporaryRectRelative$, addBboxTemporaryRectRelative$, resizeBboxTemporaryRectRelative$, bboxesRelative$, selectedId$, highlightId$, cursorType$, mode$, imgVisibleAreaRectAbsolute$, canvasRectSizeAbsolute$);
    return values;
};
exports.useAnnotationCanvasDataflow = useAnnotationCanvasDataflow;
